// Subsequence matching for the command palette.
//
// Small enough not to warrant a dependency: the corpus is ~30 menu entries, so
// this runs on every keystroke without being noticeable.

/**
 * Score `text` against `query`, or return -1 when the query's characters do not
 * appear in order. Higher is better.
 *
 * Ranking favours, in order: matching from the very start, matching at a word
 * boundary, and runs of adjacent characters. That way "us" ranks "Users" above
 * "Merchant Terminals", and "mt" still finds "Merchant Terminals".
 */
export const fuzzyScore = (text, query) => {
    if (!query) return 0;

    const haystack = text.toLowerCase();
    const needle = query.toLowerCase();

    let score = 0;
    let from = 0;
    let previousIndex = -1;

    for (const char of needle) {
        const index = haystack.indexOf(char, from);
        if (index === -1) return -1;

        if (index === 0) {
            score += 12;
        } else if (' /-_'.includes(haystack[index - 1])) {
            score += 8; // start of a word
        } else {
            score += 1;
        }

        if (index === previousIndex + 1) score += 6; // adjacent run

        previousIndex = index;
        from = index + 1;
    }

    // Prefer the shorter of two otherwise equal matches.
    return score - haystack.length * 0.05;
};

/**
 * Entries whose `searchText` matches `query`, best first. Entry order is
 * preserved when there is no query, so the palette opens showing the menu in
 * its normal order rather than an arbitrary one.
 *
 * When some entries contain the query as typed, only those are listed. A
 * subsequence spread over a section name and a path matches almost anything:
 * "curr" found Roles and Countries beside Currencies. The loose match is kept
 * for abbreviations ("mt" for Merchant Terminals), which no entry contains.
 */
export const fuzzyFilter = (entries, query) => {
    if (!query) return entries;

    const needle = query.toLowerCase();
    const literal = entries.filter((entry) =>
        entry.searchText.toLowerCase().includes(needle)
    );

    return (literal.length ? literal : entries)
        .map((entry) => ({ entry, score: fuzzyScore(entry.searchText, query) }))
        .filter((scored) => scored.score >= 0)
        .sort((a, b) => b.score - a.score)
        .map((scored) => scored.entry);
};
